import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import { HashRouter as Router } from 'react-router-dom';
import Layout from './components/Layout1.jsx';
import Login from './pages/Login.jsx';
import OfflineIndicator from './components/OfflineIndicator.jsx';
import StorageSafeModeBanner from './components/StorageSafeModeBanner.jsx';
import InventoryLedgerSafeModeBanner from './components/InventoryLedgerSafeModeBanner.jsx';
import InventoryLedgerHealthScheduler from './components/InventoryLedgerHealthScheduler.jsx';
import StorageBootstrapDiagnostic from './components/storage/StorageBootstrapDiagnostic.jsx';
import NewBusinessAppModals from './components/NewBusinessAppModals.jsx';
import './styles/layouts/page-transitions.css';
import { DataProvider } from './DataContext.jsx';
import { LanguageProvider } from './contexts/LanguageContext.jsx';
import { NotificationProvider } from './components/common/NotificationSystem.jsx';
import { RoleProvider } from './contexts/RoleContext.jsx';
import { CurrencyProvider } from './contexts/CurrencyContext.jsx';
import { LotSystemProvider } from './components/LotSystemProvider.jsx';
import { WorkerDashboardProvider } from './contexts/WorkerDashboardContext.jsx';
import { NewCashRegisterProvider } from './contexts/NewCashRegisterContext.jsx';
import { CartProvider } from './contexts/CartContext.jsx';
// initializeSupabaseTables - REMOVED: Temporarily disabled, not used
import backgroundUpdateManager from './services/backgroundUpdateManager.js';
import {
  isDeferBackgroundUpdateManagerEnabled,
  isDeferMachineBindingEnabled
} from './infrastructure/startup/bootFeatureFlags.js';
import { LOADING_DIAGNOSTIC_MS, isLoadingDiagnosticAutoShowEnabled } from './infrastructure/startup/loadingDiagnostic.js';
import { StartupIntegrityProvider } from './contexts/StartupIntegrityContext.jsx';
import { initializeBundleOptimization } from './utils/bundleOptimizer.js';
import {
  markBootGate,
  measureBoot,
  setBootMeta
} from './infrastructure/telemetry/bootTelemetry.js';
import { readAppLanguage } from './utils/readAppLanguage.js';
import { getTranslation } from './utils/constants.js';
import { emergencyFullRecovery } from './utils/emergencyDataFix.js';
import { kvGet, kvRemove } from './infrastructure/storage/kvAccess.js';
import { completeLogout } from './services/completeLogoutService.js';
import { isRegistrationAllowedOnThisInstallation } from './utils/installationRegistrationPolicy.js';
/** Keep login first paint light — route table + secondary auth load on demand. */
const ConditionalRoutes = lazy(() => import('./components/ConditionalRoutes.jsx'));
const Register = lazy(() => import('./pages/Register.jsx'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword.jsx'));
const ResetPassword = lazy(() => import('./pages/ResetPassword.jsx'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard.jsx'));

function readBootLanguage() {
  return readAppLanguage();
}

// storageEnhancement ÔÇö deaktiviert (ADR-0002, siehe localStorageHealth)

// Emergency Data Fix - Repariert korrupte Daten sofort

// Console Cleanup Commands - REMOVED: File contains only stub functions

// REMOVED: Temporarily disabled imports
// - deviationCleanup
// - errorHandler
// - autoMigrate

// Notfall-Wiederherstellung nach Storage-Init (nicht beim Modul-Load — blockiert sonst den Start)
function scheduleEmergencyRecovery() {
  const run = () => {
    try {
      emergencyFullRecovery();
    } catch {
      // Emergency recovery error handled silently
    }
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 100);
  }
}

/** Fail IndexedDB init before auth safety so we fall back to localStorage, not flash login.
 *  Large customer packs (~1GB IndexedDB blobs) need far more than 8s on first open. */
const STARTUP_STORAGE_TIMEOUT_MS = 180000;
/** Must stay above STARTUP_STORAGE_TIMEOUT_MS (+ binding/restore headroom). */
const AUTH_LOADING_SAFETY_MS = 210000;

function withStartupTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`[App] ${label} timed out after ${ms}ms`)),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/** Schwere Init nach Login-Anzeige — darf den Start nicht blockieren. */
function runDeferredStartupInit() {
  initializeBundleOptimization();

  void (async () => {
    try {
      const { autoSaveService } = await import('./services/autoSaveService');
      autoSaveService.initialize();
    } catch (e) {
      console.warn('[App] autoSave init skipped:', e?.message || e);
    }

    try {
      const { localStorageManager } = await import('./services/localStorageManager');
      localStorageManager.cleanupTemporaryKeys();
    } catch (e) {
      console.warn('[App] localStorageManager skipped:', e?.message || e);
    }

    if (process.env.NODE_ENV === 'development') {
      try {
        const { runLocalStorageFullHealthReport, summarizeAuditSeverity } = await import(
          './utils/localStorageHealth'
        );
        runLocalStorageFullHealthReport().then((report) => {
          if (!kvGet('currentUser') && !kvGet('currentBusiness')) return;
          if (report.ok) return;
          const severity = summarizeAuditSeverity(report.audit);
          console.warn('[AUDIT] KV health — critical issues only:', {
            ok: report.ok,
            auditCritical: report.auditCriticalIssues,
            auditInformational: severity.informational,
            entityOnly: report.entityOnly?.issues || null,
            startup: report.startup
          });
        });
      } catch {
        /* optional */
      }
    }

    try {
      const { enableLocalStorageCompression } = await import('./utils/localStorageIntegration.js');
      await enableLocalStorageCompression();
      const { autoMigrateOnce } = await import('./utils/compressionMigration.js');
      try {
        autoMigrateOnce();
      } catch (migrateErr) {
        console.warn('[App] Compression migration skipped:', migrateErr?.message || migrateErr);
      }
      try {
        const { recoverWorkersOnStartup } = await import('./utils/workersStorage');
        const { recoverRoleOnStartup } = await import('./utils/roleStorage');
        const recovered = recoverWorkersOnStartup();
        if (recovered.migrated && recovered.workers?.length > 0) {
          console.info(
            `[App] Recovered ${recovered.workers.length} employé(s) from legacy storage`
          );
        }
        const roleRec = recoverRoleOnStartup();
        if (roleRec.migrated && roleRec.role) {
          console.info('[App] Recovered role session from legacy storage');
        }
        const { recoverWorkerSessionsOnStartup } = await import(
          './infrastructure/storage/domainKvRepairMigration'
        );
        const sessionRepair = recoverWorkerSessionsOnStartup();
        if (sessionRepair.repaired > 0) {
          console.info(`[App] Repaired ${sessionRepair.repaired} worker session KV key(s)`);
        }
        const { recoverSequencesOnStartup } = await import(
          './infrastructure/storage/sequencesMigration'
        );
        const seqRec = recoverSequencesOnStartup();
        if (seqRec.migrated) {
          const { default: isoStandardIdGenerator } = await import(
            './utils/ISOStandardIdGenerator'
          );
          isoStandardIdGenerator.reloadSequences();
          console.info('[App] Merged offline sequence keys into business sequences');
        }
        const {
          runStartupRecovery,
          runSaleFinalizeJournalRepair,
          runSaleFinalizeJournalSweep
        } = await import('./infrastructure/storage/recoveryOrchestrator');
        const { getCurrentBusinessId } = await import('./utils/sharedCore');
        const { scanRec, blobRec } = await runStartupRecovery();
        try {
          const { runKvKeyConsolidation } = await import(
            './infrastructure/storage/kvKeyConsolidation'
          );
          const bizId = getCurrentBusinessId({ allowFallback: true });
          await runKvKeyConsolidation({ mode: 'startup', businessId: bizId });
        } catch (keyConsolidErr) {
          console.warn('[App] KV key consolidation skipped:', keyConsolidErr?.message || keyConsolidErr);
        }
        try {
          // Never replay checkout on cold start — stale journals re-deduct lots that
          // no longer exist → "Aucun lot disponible…" spam / write failures.
          await runSaleFinalizeJournalRepair(null, {
            statuses: ['pending'],
            replayCheckout: false,
            maxEntriesPerBusiness: 30
          });
        } catch (journalErr) {
          console.warn('[App] Sale finalize journal repair skipped:', journalErr?.message || journalErr);
        }
        try {
          runSaleFinalizeJournalSweep();
        } catch (journalSweepErr) {
          console.warn('[App] Sale finalize journal sweep skipped:', journalSweepErr?.message || journalSweepErr);
        }
        if (scanRec.migratedCount > 0) {
          const recoveredBizId = getCurrentBusinessId({ allowFallback: false });
          if (recoveredBizId) {
            const { dispatchSyncBusinessFieldsFromKv } = await import('./utils/businessStateSync');
            dispatchSyncBusinessFieldsFromKv(recoveredBizId);
          }
        }
        if (blobRec.migrated) {
          const recoveredBizId = getCurrentBusinessId({ allowFallback: false });
          if (recoveredBizId) {
            const { dispatchSyncBusinessFieldsFromKv } = await import('./utils/businessStateSync');
            dispatchSyncBusinessFieldsFromKv(recoveredBizId);
          }
        }
        // Sync-Drain bewusst nicht beim Start — useBackgroundSyncLifecycle übernimmt (kein Supabase-Loop)
        try {
          const { runLocalStoragePostRecoveryCleanup } = await import('./utils/localStorageHealth');
          await runLocalStoragePostRecoveryCleanup();
        } catch (postCleanErr) {
          console.warn('[App] Post-recovery KV cleanup skipped:', postCleanErr?.message || postCleanErr);
        }
      } catch (recoverErr) {
        console.warn('[App] Storage recovery skipped:', recoverErr?.message || recoverErr);
      }
    } catch (e) {
      console.warn('[App] KV compression facade init skipped:', e?.message || e);
    }

    try {
      const { machineBindingService } = await import('./services/machineBindingService');
      machineBindingService.syncQueue();
    } catch {
      /* optional */
    }
  })();
}

// Simple Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Login is now handled exclusively through Supabase in Login.jsx
  // No localStorage fallback login

  const logout = useCallback(async () => {
    await completeLogout({ reload: false });
  }, []);

  // Registration is now handled exclusively through Supabase in Register.jsx
  // No localStorage fallback registration

  // Initialize auth on app start
  useEffect(() => {
    let cancelled = false;
    let settled = false;
    let loadingTimeout = null;
    if (typeof window !== 'undefined') {
      window.__AMAN_AUTH_LOADING = true;
    }

    const finishAuthLoading = () => {
      if (cancelled || settled) return;
      settled = true;
      if (loadingTimeout != null) clearTimeout(loadingTimeout);
      if (typeof window !== 'undefined') {
        window.__AMAN_AUTH_LOADING = false;
      }
      markBootGate('auth_loading_done');
      measureBoot('react_bootstrap_start', 'auth_loading_done', 'bootstrap_to_auth_done');
      setIsLoading(false);
    };

    /** Best-effort sync restore so a hung init does not flash Login → Dashboard. */
    const trySyncRestoreUser = () => {
      try {
        const stored = kvGet('currentUser');
        if (!stored) return;
        let user = stored;
        if (typeof stored === 'string') {
          if (
            stored === '[object Object]' ||
            stored === '"[object Object]"' ||
            stored.trim() === ''
          ) {
            return;
          }
          user = JSON.parse(stored);
        }
        if (user && typeof user === 'object') {
          setCurrentUser(user);
        }
      } catch {
        /* ignore — full init path still owns cleanup */
      }
    };

    loadingTimeout = setTimeout(() => {
      if (cancelled || settled) return;
      console.warn('[App] Auth init timeout — unlocking UI');
      trySyncRestoreUser();
      finishAuthLoading();
    }, AUTH_LOADING_SAFETY_MS);

    const restoreStoredUser = async (storedUser) => {
      if (!storedUser) return;
      let user;
      if (typeof storedUser === 'string') {
        if (
          storedUser === '[object Object]' ||
          storedUser === '"[object Object]"' ||
          storedUser.trim() === ''
        ) {
          console.warn('[APP] Corrupted currentUser data in initializeAuth, cleaning up');
          kvRemove('currentUser');
          return;
        }
        try {
          user = JSON.parse(storedUser);
        } catch (parseError) {
          console.warn('Error parsing currentUser:', parseError);
          kvRemove('currentUser');
          return;
        }
      } else if (typeof storedUser === 'object' && storedUser !== null) {
        user = storedUser;
      } else {
        return;
      }
      const { normalizeStoredSessionUser, buildOfflineCurrentBusiness } = await import(
        './services/offlineAuthSession'
      );
      const normalizedUser = normalizeStoredSessionUser(user);
      if (normalizedUser !== user) {
        const { kvSet } = await import('./infrastructure/storage/kvAccess');
        kvSet('currentUser', normalizedUser);
        if (normalizedUser?.business) {
          kvSet('currentBusiness', buildOfflineCurrentBusiness(normalizedUser.business));
        }
      }
      if (!cancelled) {
        setCurrentUser(normalizedUser);
      }
    };

    const initializeAuth = async () => {
      try {
        markBootGate('auth_effect_start');
        // Phase 1: IndexedDB KV — vor jedem kvGet/kvSet (mit Timeout, damit UI nicht hängt)
        try {
          markBootGate('storage_engine_start');
          const { getPhase1BootstrapOptions } = await import(
            './infrastructure/storage/storagePhase1Meta'
          );
          const phase1 = getPhase1BootstrapOptions();
          const { initStorageEngine } = await import('./infrastructure/storage/initStorageEngine');
          await withStartupTimeout(
            initStorageEngine(phase1),
            STARTUP_STORAGE_TIMEOUT_MS,
            'IndexedDB storage init'
          );
          markBootGate('storage_engine_ready');
          measureBoot('storage_engine_start', 'storage_engine_ready', 'storage_engine_init');
          if (typeof window !== 'undefined') {
            window.__AMAN_STORAGE_ENGINE_READY = true;
          }
          scheduleEmergencyRecovery();
          try {
            const { startLocalCloudRegisterAutoFlush } = await import(
              './services/upgradeLocalBusinessToCloud'
            );
            startLocalCloudRegisterAutoFlush();
          } catch {
            /* optional */
          }
          if (process.env.NODE_ENV === 'development') {
            const { registerStorageDevTools } = await import(
              './infrastructure/storage/storageDevTools'
            );
            registerStorageDevTools();
          }
        } catch (storageErr) {
          console.warn(
            '[App] IndexedDB storage init failed, using localStorage:',
            storageErr?.message || storageErr
          );
          try {
            const { abandonStorageInit } = await import(
              './infrastructure/storage/initStorageEngine'
            );
            abandonStorageInit();
          } catch {
            /* ignore */
          }
          const { configureStorageEngine } = await import('./app/di/container');
          configureStorageEngine();
          if (typeof window !== 'undefined') {
            window.__AMAN_STORAGE_ENGINE_READY = true;
          }
          try {
            const { startLocalCloudRegisterAutoFlush } = await import(
              './services/upgradeLocalBusinessToCloud'
            );
            startLocalCloudRegisterAutoFlush();
          } catch {
            /* optional */
          }
        }

        const { consumeBusinessLogoutIntent } = await import('./utils/businessLogoutFlag');
        if (consumeBusinessLogoutIntent()) {
          const { persistentlyClearBusinessSession } = await import('./services/completeLogoutService');
          await persistentlyClearBusinessSession();
          setCurrentUser(null);
          return;
        }

        const { isBusinessLogoutInProgress } = await import('./utils/businessLogoutFlag');
        if (isBusinessLogoutInProgress()) {
          setCurrentUser(null);
          return;
        }

        const storedUserEarly = kvGet('currentUser');

        // Frische Installation / kein Konto: Login sofort zeigen, Rest im Hintergrund
        if (!storedUserEarly) {
          runDeferredStartupInit();
          return;
        }

        if (!isDeferMachineBindingEnabled()) {
          const { runStartupMachineBinding } = await import('./services/startupMachineBinding');
          markBootGate('machine_binding_start');
          const bindingResult = await runStartupMachineBinding({ storedUserEarly });
          markBootGate('machine_binding_done', { canProceed: bindingResult.canProceed });
          measureBoot('machine_binding_start', 'machine_binding_done', 'machine_binding');
          if (!bindingResult.canProceed && bindingResult.blocked) {
            alert(
              bindingResult.reason || 'Diese App ist an einen anderen Computer gebunden.'
            );
            kvRemove('currentUser');
            kvRemove('currentBusiness');
            setCurrentUser(null);
            return;
          }
        }

        if (isBusinessLogoutInProgress()) {
          setCurrentUser(null);
          return;
        }

        try {
          await restoreStoredUser(storedUserEarly);
          markBootGate('auth_session_restore_done');
        } catch (error) {
          console.warn('Error parsing currentUser:', error);
          kvRemove('currentUser');
        }

        runDeferredStartupInit();
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        finishAuthLoading();
      }
    };

    initializeAuth();

    // Listen for auth state changes from login / logout
    const handleAuthStateChange = (event) => {
      const user = event.detail?.user ?? null;
      setCurrentUser(user);
      // Logout-Intent NICHT hier löschen — sonst öffnet CodeEntry vor Login.
      // Intent wird auf der Login-Seite (mount) bzw. bei erfolgreichem Login entfernt.
    };

    window.addEventListener('authStateChanged', handleAuthStateChange);

    return () => {
      cancelled = true;
      clearTimeout(loadingTimeout);
      window.removeEventListener('authStateChanged', handleAuthStateChange);
    };
  }, []);

  const authValue = useMemo(
    () => ({ currentUser, isLoading, logout }),
    [currentUser, isLoading, logout]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// BusinessAppModals is rendered via NewBusinessAppModals in the app tree.

function AppContent() {
  const { currentUser, isLoading, logout } = useAuth();
  const [currentView, setCurrentView] = useState('login');
  const [authLoadingDiagnostic, setAuthLoadingDiagnostic] = useState(false);
  /** Keep DataProvider mounted after first business login for process lifetime. */
  const [retainBusinessDataShell, setRetainBusinessDataShell] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setAuthLoadingDiagnostic(false);
      return undefined;
    }
    if (!isLoadingDiagnosticAutoShowEnabled()) return undefined;
    const timer = setTimeout(() => setAuthLoadingDiagnostic(true), LOADING_DIAGNOSTIC_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (currentUser && currentUser.type !== 'admin') {
      setRetainBusinessDataShell(true);
    }
  }, [currentUser]);

  // Handle auth state changes to update currentView
  useEffect(() => {
    if (currentUser && (currentView === 'login' || currentView === 'register')) {
      setCurrentView('dashboard');
    } else if (!currentUser && currentView === 'dashboard') {
      setCurrentView('login');
    }
  }, [currentUser, currentView]);

  // Online ÔåÆ Outbox + Reports drain (Browser + Electron)
  useEffect(() => {
    const handleOnline = async () => {
      try {
        const { drainPendingSyncWithRetry } = await import(
          './infrastructure/storage/drainPendingSyncWithRetry'
        );
        await drainPendingSyncWithRetry({ limit: 50 });
      } catch {
        /* sync optional */
      }
    };

    window.addEventListener('online', handleOnline);

    const handleOffline = () => {
      /* offline ÔÇö Outbox bleibt pending */
    };

    if (window.electronAPI?.onOnline) {
      window.electronAPI.onOnline(handleOnline);
      window.electronAPI.onOffline(handleOffline);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      if (window.electronAPI?.removeOnlineListener) {
        window.electronAPI.removeOnlineListener();
        window.electronAPI.removeOfflineListener();
      }
    };
  }, []);

  useEffect(() => {
    // Check if this is a reset password link
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const resetEmail = urlParams.get('email');
    
    if (resetToken && resetEmail) {
      setCurrentView('resetPassword');
    }
  }, []);

  // Show loading screen while initializing
  if (isLoading) {
    if (authLoadingDiagnostic) {
      return (
        <StorageBootstrapDiagnostic context="auth" dataReady={false} dataLoaded={false} roleReady={false} />
      );
    }
    return (
      <div className="app-loading-screen" role="status">
        {getTranslation('conditionalRoutes.loadingApp', readBootLanguage())}
      </div>
    );
  }

  const renderUnauthenticatedAuthView = () => {
    if (currentView === 'register' && !isRegistrationAllowedOnThisInstallation()) {
      return (
        <Login
          registrationAllowed={false}
          onShowForgotPassword={() => setCurrentView('forgotPassword')}
        />
      );
    }
    if (currentView === 'register') {
      return (
        <Suspense fallback={<div className="app-loading-screen" role="status" />}>
          <Register onBack={() => setCurrentView('login')} />
        </Suspense>
      );
    }
    if (currentView === 'forgotPassword') {
      return (
        <Suspense fallback={<div className="app-loading-screen" role="status" />}>
          <ForgotPassword onBack={() => setCurrentView('login')} />
        </Suspense>
      );
    }
    if (currentView === 'resetPassword') {
      return (
        <Suspense fallback={<div className="app-loading-screen" role="status" />}>
          <ResetPassword onBack={() => setCurrentView('login')} />
        </Suspense>
      );
    }
    return (
      <Login
        registrationAllowed={isRegistrationAllowedOnThisInstallation()}
        onShowRegister={() => setCurrentView('register')}
        onShowForgotPassword={() => setCurrentView('forgotPassword')}
      />
    );
  };

  // Admin — unified enterprise shell (P1-3)
  if (currentUser?.type === 'admin') {
    return (
      <div className="app-shell-enter">
        <Router future={{ v7_relativeSplatPath: true }}>
          <NotificationProvider>
            <LanguageProvider>
              <DataProvider>
                <OfflineIndicator />
                <Suspense
                  fallback={
                    <div className="app-loading-screen" role="status">
                      {getTranslation('conditionalRoutes.loadingApp', readBootLanguage())}
                    </div>
                  }
                >
                  <Layout variant="admin" onAdminLogout={logout}>
                    <AdminDashboard onLogout={logout} shellEmbedded />
                  </Layout>
                </Suspense>
              </DataProvider>
            </LanguageProvider>
          </NotificationProvider>
        </Router>
      </div>
    );
  }

  // Business shell: keep DataProvider mounted for process lifetime after first login
  // (Electron soft UI swap — data still clears + reloads; no warm soft-login skip).
  const showRetainedBusinessShell =
    retainBusinessDataShell || (currentUser && currentUser.type !== 'admin');

  if (showRetainedBusinessShell) {
    return (
      <div className="app-shell-enter">
            {/* Route handoff is synchronous: lazy destinations show their shell
                immediately instead of retaining the previous page while loading. */}
            <Router future={{ v7_relativeSplatPath: true }}>
          <DataProvider>
            {currentUser ? (
              <NotificationProvider>
                <StartupIntegrityProvider>
                  <RoleProvider>
                    <CurrencyProvider>
                      <LotSystemProvider>
                        <WorkerDashboardProvider>
                          <NewCashRegisterProvider>
                            <CartProvider>
                              <OfflineIndicator />
                              <StorageSafeModeBanner />
                              <InventoryLedgerSafeModeBanner />
                              <InventoryLedgerHealthScheduler />
                              <Layout>
                                <Suspense
                                  fallback={
                                    <div className="route-shell-skeleton" role="status">
                                      <p className="route-shell-skeleton__message">
                                        {getTranslation(
                                          'conditionalRoutes.loadingApp',
                                          readBootLanguage()
                                        )}
                                      </p>
                                    </div>
                                  }
                                >
                                  <ConditionalRoutes />
                                </Suspense>
                              </Layout>
                              <NewBusinessAppModals />
                            </CartProvider>
                          </NewCashRegisterProvider>
                        </WorkerDashboardProvider>
                      </LotSystemProvider>
                    </CurrencyProvider>
                  </RoleProvider>
                </StartupIntegrityProvider>
              </NotificationProvider>
            ) : (
              <NotificationProvider>
                <OfflineIndicator />
                {renderUnauthenticatedAuthView()}
              </NotificationProvider>
            )}
          </DataProvider>
        </Router>
      </div>
    );
  }

  // Cold start — never logged in this process: login without DataProvider
  return (
    <NotificationProvider>
      <OfflineIndicator />
      {renderUnauthenticatedAuthView()}
    </NotificationProvider>
  );
}

function App() {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__AMAN_REACT_MOUNTED = true;
      window.__AMAN_AUTH_LOADING = true;
      window.__AMAN_STORAGE_ENGINE_READY = false;
    }
  }, []);

  // System-Initialisierung
  useEffect(() => {
    const initBackgroundUpdates = () => backgroundUpdateManager.initialize();
    if (isDeferBackgroundUpdateManagerEnabled()) {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(initBackgroundUpdates, { timeout: 3000 });
      } else {
        setTimeout(initBackgroundUpdates, 100);
      }
    } else {
      initBackgroundUpdates();
    }

    return () => {
      // Cleanup on unmount — Master Worker Pool disabled
    };
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
